import { NavLink, Link, useLocation } from "react-router-dom";
import { LOGO_URL, LOGO_FORENSE, NAV_ITEMS, WHATSAPP_URL, INSTAGRAM_URL, FACEBOOK_URL, YOUTUBE_URL } from "@/lib/brand";
import { Youtube, Instagram, Facebook, MessageCircle } from "lucide-react";
import WhatsAppFloat from "@/components/WhatsAppFloat";

const Layout = ({ children }) => {
  const location = useLocation();
  const isForense = location.pathname === "/psicologia-forense";

  return (
    <div
      data-testid="app-layout"
      className={`min-h-screen w-full flex ${isForense ? "forense-bg" : ""}`}
      style={!isForense ? { background: "var(--p-cream)" } : undefined}
    >
      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className="hidden lg:flex flex-col w-[190px] shrink-0 sticky top-0 h-screen"
        style={{
          background: "var(--p-burgundy)",
          color: "var(--p-cream)",
        }}
      >
        <Link to="/" data-testid="sidebar-logo-link" className="flex flex-col items-center justify-center py-7 gap-2">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center"
            style={{
              background: isForense
                ? "radial-gradient(circle at 30% 30%, #f5efe6, #d8c48a 60%, #b8975a 100%)"
                : "var(--p-cream)",
            }}
          >
            <img
              src={isForense ? LOGO_FORENSE : LOGO_URL}
              alt="Psychelibrium"
              className="w-12 h-12 object-contain"
            />
          </div>
          <div
            className="brand-title text-[15px] mt-1"
            style={{ color: "var(--p-gold-soft)" }}
          >
            Psychelibrium
          </div>
          <div
            className="brand-sub"
            style={{ color: "rgba(245,239,230,0.75)" }}
          >
            Psicología Clínica<br />y Forense
          </div>
        </Link>
        <div className="rule-gold mx-6 mb-3" />
        <nav className="flex-1 flex flex-col justify-center gap-1 px-3">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.to.replace("/", "")}`}
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-4 pb-6 pt-2">
          <div className="rule-gold mb-4" />
          <p
            className="script-font text-center text-2xl leading-tight"
            style={{ color: "var(--p-gold-soft)" }}
          >
            Comprender<br />para Transformar
          </p>
          <p
            className="text-center mt-4 text-[10px] tracking-[0.3em] uppercase"
            style={{ color: "rgba(245,239,230,0.6)", fontFamily: "Cinzel, serif" }}
          >
            5 a 9 pm · Online
          </p>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar (mobile menu + CTA) */}
        <TopBar />
        <main data-testid="main-content" className="flex-1">
          {children}
        </main>

        {/* Footer */}
        <footer
          data-testid="site-footer"
          className={`relative mt-16 ${isForense ? "" : ""}`}
          style={
            isForense
              ? { background: "#0b0b0c", color: "#e2c890" }
              : { background: "var(--p-burgundy)", color: "var(--p-cream)" }
          }
        >
          <div className="px-6 md:px-14 py-10 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="flex items-center gap-4 justify-center md:justify-start">
              <img
                src={isForense ? LOGO_FORENSE : LOGO_URL}
                alt="Psychelibrium"
                className="w-12 h-12 object-contain rounded-full"
                style={isForense ? { background: "linear-gradient(135deg,#f5efe6,#cdb27b)", padding: 6 } : { background: "var(--p-cream)", padding: 4 }}
              />
              <div>
                <div className="brand-title text-xl leading-none">Psychelibrium</div>
                <div className="brand-sub mt-1" style={{ color: isForense ? "#b8975a" : "var(--p-gold-soft)" }}>
                  Psicología Clínica y Forense
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 justify-center md:justify-end">
              <SocialIcon href={YOUTUBE_URL} label="youtube"><Youtube size={18} /></SocialIcon>
              <SocialIcon href={INSTAGRAM_URL} label="instagram"><Instagram size={18} /></SocialIcon>
              <SocialIcon href={FACEBOOK_URL} label="facebook"><Facebook size={18} /></SocialIcon>
              <SocialIcon href={WHATSAPP_URL} label="whatsapp"><MessageCircle size={18} /></SocialIcon>
            </div>
          </div>
          <div className="px-6 md:px-14 pb-6 pt-2 text-center space-y-3">
            <div
              className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-[11px] tracking-[0.22em] uppercase opacity-90"
              style={{ fontFamily: "Cinzel, serif" }}
            >
              <Link data-testid="footer-aviso-link" to="/aviso-de-privacidad" style={{ color: "inherit" }}>
                Aviso de Privacidad
              </Link>
              <span style={{ opacity: 0.4 }}>·</span>
              <Link data-testid="footer-confid-link" to="/politica-de-confidencialidad" style={{ color: "inherit" }}>
                Política de Confidencialidad
              </Link>
              <span style={{ opacity: 0.4 }}>·</span>
              <Link data-testid="footer-cuestionario-link" to="/cuestionario" style={{ color: "inherit" }}>
                Cuestionario previo
              </Link>
              <span style={{ opacity: 0.4 }}>·</span>
              <a data-testid="footer-agenda-link" href="/agenda" style={{ color: "inherit" }}>
                psychelibrium.com/agenda
              </a>
            </div>
            <div
              className="text-[11px] tracking-[0.25em] uppercase opacity-75"
              style={{ fontFamily: "Cinzel, serif" }}
            >
              © {new Date().getFullYear()} Psychelibrium · Atención exclusivamente en línea
            </div>
          </div>
        </footer>
      </div>
      <WhatsAppFloat />
    </div>
  );
};

const SocialIcon = ({ href, label, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer"
    data-testid={`social-${label}`}
    className="w-10 h-10 rounded-full flex items-center justify-center transition-all"
    style={{
      background: "rgba(255,255,255,0.08)",
      color: "inherit",
      border: "1px solid rgba(255,255,255,0.14)",
    }}
    onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(184,151,90,0.25)")}
    onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.08)")}
  >
    {children}
  </a>
);

const TopBar = () => {
  const location = useLocation();
  const isForense = location.pathname === "/psicologia-forense";
  return (
    <div
      data-testid="top-bar"
      className="w-full flex items-center justify-between px-4 md:px-10 py-4 sticky top-0 z-30 backdrop-blur"
      style={{
        background: isForense ? "rgba(15,15,16,0.85)" : "rgba(245,239,230,0.85)",
        borderBottom: isForense ? "1px solid rgba(184,151,90,0.2)" : "1px solid rgba(74,21,33,0.08)",
      }}
    >
      <Link to="/" data-testid="topbar-brand" className="flex items-center gap-3">
        <img
          src={isForense ? LOGO_FORENSE : LOGO_URL}
          alt="Psychelibrium"
          className="w-10 h-10 object-contain rounded-full"
          style={{ background: isForense ? "linear-gradient(135deg,#f5efe6,#cdb27b)" : "var(--p-cream)", padding: 3 }}
        />
        <div className="hidden sm:block">
          <div
            className="brand-title text-xl leading-none"
            style={{ color: isForense ? "#e2c890" : "var(--p-burgundy)" }}
          >
            Psychelibrium
          </div>
          <div
            className="brand-sub mt-1"
            style={{ color: isForense ? "#b8975a" : "var(--p-brown)" }}
          >
            Psicología Clínica y Forense
          </div>
        </div>
      </Link>
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noreferrer"
        data-testid="topbar-cta"
        className={isForense ? "btn-gold" : "btn-primary"}
      >
        Agenda tu Cita
      </a>
    </div>
  );
};

export default Layout;
